import { useThemeProps } from '@mui/material';
import { OverrideProps } from '@mui/material/OverridableComponent';
import {
  default as MuiTab,
  TabTypeMap as MuiTabTypeMap,
} from '@mui/material/Tab';
import React from 'react';
import {
  ExtendTypeMap,
  forwardRefToOverridableComponent,
} from 'src/utils/forwardRefToOverridableComponent';

export interface TabOwnProps {
  /**
   * @dart-deprecated In Dart, use `..dom.href` alongside `..component = Dom.a.elementType` instead.
   */
  href?: string;

  // Override `any` from MuiTabProps, so that the type matches that of TabContext
  // and TabPanel.
  /** The value of the corresponding `TabPanel`. */
  value?: string;
}
export type TabTypeMap<
  AdditionalProps = unknown,
  RootComponent extends React.ElementType = MuiTabTypeMap['defaultComponent']
> = ExtendTypeMap<
  {
    props: Omit<MuiTabTypeMap['props'], 'value' | 'disableFocusRipple'>;
    defaultComponent: MuiTabTypeMap['defaultComponent'];
  },
  AdditionalProps & TabOwnProps,
  RootComponent
>;

export type TabProps<
  RootComponent extends React.ElementType = MuiTabTypeMap['defaultComponent'],
  AdditionalProps = unknown
> = OverrideProps<TabTypeMap<AdditionalProps, RootComponent>, RootComponent>;

//
// This wrapper exists mainly to override the type of the `value` prop.
//

/**
 * A Tab represents a single selectable item within a TabList.
 *
 * Demos:
 *
 * - [Tabs](https://unify.workiva.org/components/tabs?tab=code)
 *
 * API:
 *
 * - [Tab API](https://unify.workiva.org/components/tabs?tab=props#tab)
 */
export const Tab = forwardRefToOverridableComponent<TabTypeMap, TabProps>(
  function Tab(inProps, forwardedRef) {
    const props = useThemeProps({ props: inProps, name: 'MuiTab' });
    const { ...rest } = props;
    return <MuiTab ref={forwardedRef} {...rest} />;
  }
);

export default Tab;
